import { useState, useEffect } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { User, Shield, Users, BellPlus, Clock, ArrowLeft, ChevronDown, Check, X } from "lucide-react"
import styles from "./WorkRequest.module.scss"
import { connect } from "react-redux"
import { openIdentityModal } from "../../store/actions"
import { fireAnalytics } from "../../utils/firebase"
import  OfertaDeTrabajo  from "../MercadoDeTrabajo/OfertaDeTrabajo.tsx"
import Logo from "../../assets/images/LogoEdify1.png"
import { getSessionId, getDeviceCategory, getPreviousPage } from "../../utils/analyticsHelpers.ts"

const WorkRequest = () => {
  const [isPhone, setIsPhone] = useState(false)
  const [selectedOption, setSelectedOption] = useState(null)
  const [showModal, setShowModal] = useState(null)
  const [showComparison, setShowComparison] = useState(false)

  useEffect(() => {
    
    // Nuevo evento estandarizado
    fireAnalytics("page_work_bag_view", {
      session_id: getSessionId(),
      source: getPreviousPage() ? "navigation" : "direct",
      previous_page: getPreviousPage(),
      device_category: getDeviceCategory(),
      category: "navigation",
      action: "page_view",
      page_name: "work_bag"
    });
  }, [])

  const handleResize = () => {
    const windowWidth = window.innerWidth
    setIsPhone(windowWidth <= 768)
  }

  useEffect(() => {
    handleResize()
    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  const handleCreateWithAccount = () => {
    setShowModal("with-account")
  }

  const handleCreateWithoutAccount = () => {
    setShowModal("without-account")
  }

  const handleContinue = () => {
    
    // Nuevo evento estandarizado
    fireAnalytics("page_work_offers_view", {
      session_id: getSessionId(),
      source: "navigation",
      previous_page: "/bolsa-trabajo",
      device_category: getDeviceCategory(),
      category: "navigation",
      action: "page_view",
      page_name: "work_offers"
    });
    
    setSelectedOption(showModal)
    setShowModal(null)
  }

  const handleBack = () => {
    setSelectedOption(null)
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1,
      },
    },
    exit: {
      opacity: 0,
      transition: { duration: 0.3 },
    },
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: "easeOut" },
    },
  }

  const buttonVariants = {
    hover: {
      scale: 1.02,
      transition: { duration: 0.2, ease: "easeOut" },
    },
    tap: {
      scale: 0.98,
      transition: { duration: 0.1 },
    },
  }

  const modalVariants = {
    hidden: {
      opacity: 0,
      scale: 0.95,
      y: 20,
    },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: {
        duration: 0.2,
        ease: "easeOut",
      },
    },
    exit: {
      opacity: 0,
      scale: 0.95,
      y: 20,
      transition: {
        duration: 0.15,
        ease: "easeIn",
      },
    },
  }

  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.2 } },
    exit: { opacity: 0, transition: { duration: 0.15 } },
  }

  const comparisonVariants = {
    hidden: {
      opacity: 0,
      height: 0,
    },
    visible: {
      opacity: 1,
      height: "auto",
      transition: {
        duration: 0.4,
        ease: "easeOut",
        staggerChildren: 0.1,
      },
    },
    exit: {
      opacity: 0,
      height: 0,
      transition: {
        duration: 0.3,
        ease: "easeIn",
      },
    },
  }

  if (selectedOption === "without-account") {
    return (
      <motion.div
        className={styles.container}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <motion.button
          className={styles.backButton}
          onClick={handleBack}
          whileHover={{ color: "#111827", x: -4 }}
          transition={{ duration: 0.2 }}
        >
          <ArrowLeft size={16} />
          Volver
        </motion.button>
        
        <OfertaDeTrabajo isStandalone/>
        
      </motion.div>
    )
  }

  if (selectedOption === "with-account") {
    return (
      <motion.div
        className={styles.container}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <motion.button
          className={styles.backButton}
          onClick={handleBack}
          whileHover={{ color: "#111827", x: -4 }}
          transition={{ duration: 0.2 }}
        >
          <ArrowLeft size={16} />
          Volver
        </motion.button>
        <motion.div className={styles.authContainer} variants={containerVariants} initial="hidden" animate="visible">
          <motion.h1 className={styles.authTitle} variants={itemVariants}>
            Accede a Edify
          </motion.h1>
          <motion.p className={styles.authSubtitle} variants={itemVariants}>
            Panel centralizado para gestionar ofertas, candidatos y estadísticas
          </motion.p>
          <motion.div className={styles.authButtons} variants={itemVariants}>
            <a
              href="https://app.edify.la/register"
              target="_blank"
              rel="noopener noreferrer"
              className={styles.authLink}
            >
              <motion.button
                className={styles.quickButton}
                variants={buttonVariants}
                whileHover="hover"
                whileTap="tap"
              >
                Crear cuenta
              </motion.button>
            </a>
            <a href="https://app.edify.la/" target="_blank" rel="noopener noreferrer" className={styles.authLink}>
              <motion.button className={styles.quickButtonSecondary} variants={buttonVariants} whileHover="hover" whileTap="tap">
                Iniciar sesión
              </motion.button>
            </a>
          </motion.div>
        </motion.div>
      </motion.div>
    )
  }

  return (
    <motion.div className={styles.container} variants={containerVariants} initial="hidden" animate="visible">
      <div style={{ margin: "auto"}}>
      <motion.div className={styles.logoContainer} variants={itemVariants}>
        <img src={Logo} alt="Edify" className={styles.logo} />
      </motion.div>

      <motion.h1 className={styles.title} variants={itemVariants}>
        Publica tu oferta
      </motion.h1>
      <motion.p className={styles.subtitle} variants={itemVariants}>
        Comienza ahora
      </motion.p>

      <motion.div className={styles.quickActions} variants={itemVariants}>
        <div className={styles.quickButtonContainer}>
          <motion.button
            className={styles.quickButton}
            onClick={handleCreateWithAccount}
            variants={buttonVariants}
            whileHover="hover"
            whileTap="tap"
          >
            Crear con cuenta
          </motion.button>
        </div>

        <div className={styles.quickButtonContainer}>
          <motion.button
            className={styles.quickButtonSecondary}
            onClick={handleCreateWithoutAccount}
            variants={buttonVariants}
            whileHover="hover"
            whileTap="tap"
          >
            Crear sin cuenta
          </motion.button>
        </div>
      </motion.div>
      <AnimatePresence>
        {showModal && (
          <motion.div
            className={styles.modalOverlay}
            variants={overlayVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={() => setShowModal(null)}
          >
            <motion.div
              className={styles.modal}
              variants={modalVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className={styles.modalTitle}>{showModal === "with-account" ? "Con cuenta Edify" : "Sin cuenta"}</h3>

              {showModal === "with-account" && <div className={styles.modalBadge}>Recomendado</div>}

              <p className={styles.modalDescription}>
                {showModal === "with-account"
                  ? "Acceso completo a candidatos verificados y panel de gestión centralizado."
                  : "Publicación rápida y temporal sin necesidad de registro."}
              </p>

              <ul className={styles.modalFeatureList}>
                {showModal === "with-account" ? (
                  <>
                    <li className={styles.modalFeature}>
                      <Check size={16} color="#10b981" />
                      Candidatos con identidad verificada
                    </li>
                    <li className={styles.modalFeature}>
                      <Check size={16} color="#10b981" />
                      Archivos adjuntos y CVs
                    </li>
                    <li className={styles.modalFeature}>
                      <Check size={16} color="#10b981" />
                      Notificaciones en tiempo real
                    </li>
                  </>
                ) : (
                  <>
                    <li className={styles.modalFeature}>
                      <Check size={16} color="#10b981" />
                      Publicación inmediata
                    </li>
                    <li className={styles.modalFeatureLimited}>
                      <X size={16} color="#ef4444" />
                      Candidatos no verificados
                    </li>
                    <li className={styles.modalFeatureLimited}>
                      <X size={16} color="#ef4444" />
                      Avisos por correo electrónico
                    </li>
                  </>
                )}
              </ul>

              <div className={styles.modalButtons}>
                <motion.button
                  className={styles.modalButtonPrimary}
                  onClick={handleContinue}
                  variants={buttonVariants}
                  whileHover="hover"
                  whileTap="tap"
                >
                  Continuar
                </motion.button>
                <motion.button
                  className={styles.modalButtonSecondary}
                  onClick={() => setShowModal(null)}
                  variants={buttonVariants}
                  whileHover="hover"
                  whileTap="tap"
                >
                  Volver
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        className={styles.compareToggle}
        onClick={() => setShowComparison(!showComparison)}
        variants={itemVariants}
        whileHover={{ color: "#111827" }}
      >
        {showComparison ? "Ocultar comparación detallada" : "Ver comparación detallada"}
        <motion.div animate={{ rotate: showComparison ? 180 : 0 }} transition={{ duration: 0.2 }}>
          <ChevronDown size={16} />
        </motion.div>
      </motion.button>

      <AnimatePresence>
        {showComparison && (
          <motion.div
            className={styles.comparisonContainer}
            variants={comparisonVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <motion.h2 className={styles.compareTitle} variants={itemVariants}>
              Comparación detallada
            </motion.h2>

            <motion.div className={styles.optionsContainer} variants={containerVariants}>
              <motion.div className={styles.optionRecommended} variants={itemVariants}>
                <h3 className={styles.optionTitle}>
                  <User size={16} />
                  Con cuenta
                  <span className={styles.optionBadge}>Recomendado</span>
                </h3>
                <p className={styles.optionDescription}>Acceso completo a candidatos verificados y panel de gestión</p>
                <ul className={styles.featureList}>
                  <li className={styles.feature}>
                    <Shield size={12} color="#10b981" />
                    Candidatos con identidad verificada
                  </li>
                  <li className={styles.feature}>
                    <Users size={12} color="#10b981" />
                    Archivos adjuntos y CVs
                  </li>
                  <li className={styles.feature}>
                    <BellPlus size={12} color="#10b981" />
                    Notificaciones en tiempo real
                  </li>
                </ul>
              </motion.div>

              <motion.div className={styles.option} variants={itemVariants}>
                <h3 className={styles.optionTitle}>
                  <Clock size={16} />
                  Sin cuenta
                </h3>
                <p className={styles.optionDescription}>Publicación rápida y temporal sin necesidad de registro</p>
                <ul className={styles.featureList}>
                  <li className={styles.feature}>
                    <Clock size={12} color="#10b981" />
                    Publicación inmediata
                  </li>
                  <li className={styles.featureLimited}>
                    <Users size={12} color="#9ca3af" />
                    Candidatos no verificados
                  </li>
                  <li className={styles.featureLimited}>
                    <BellPlus size={12} color="#9ca3af" />
                    Avisos por correo electrónico
                  </li>
                </ul>
              </motion.div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      </div>
    </motion.div>
  )
}

const mapStateToProps = (state) => {
  return {
    skills: state.register.skills,
    workOfferModal: state.general.workOfferModal,
  }
}

export default connect(mapStateToProps, { openIdentityModal })(WorkRequest);